import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ThreadSortBy, GetThreadsParams } from '@/types/residents/corkBoard';
import { CORK_BOARD_PAGINATION } from '@/constants/residents/corkBoardConstants';

const SEARCH_DEBOUNCE_MS = 400;

interface UseCorkBoardFiltersOptions {
  propertyId: number | undefined;
  kind?: 'thread' | 'survey';
}

export const useCorkBoardFilters = ({ propertyId, kind }: UseCorkBoardFiltersOptions) => {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<ThreadSortBy>('lastActivity');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState<number>(CORK_BOARD_PAGINATION.defaultPage);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    setPage(CORK_BOARD_PAGINATION.defaultPage);
  }, [selectedTagIds, debouncedSearch]);

  const toggleTag = useCallback((tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  }, []);

  const clearTags = useCallback(() => {
    setSelectedTagIds([]);
  }, []);

  const handleSetSortBy = useCallback((value: ThreadSortBy) => {
    setSortBy(value);
    setPage(CORK_BOARD_PAGINATION.defaultPage);
  }, []);

  const setSearch = useCallback((text: string) => {
    setSearchInput(text);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedTagIds([]);
    setSortBy('lastActivity');
    setSearchInput('');
    setDebouncedSearch('');
    setPage(CORK_BOARD_PAGINATION.defaultPage);
  }, []);

  const loadNextPage = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const resetPage = useCallback(() => {
    setPage(CORK_BOARD_PAGINATION.defaultPage);
  }, []);

  const hasActiveFilters = selectedTagIds.length > 0 || debouncedSearch.length > 0;

  const accumulationKey = useMemo(
    () => `${kind ?? 'all'}_${propertyId}_${[...selectedTagIds].sort().join(',')}_${sortBy}_${debouncedSearch}`,
    [kind, propertyId, selectedTagIds, sortBy, debouncedSearch],
  );

  const queryParams = useMemo<GetThreadsParams>(() => ({
    propertyId: propertyId ?? 0,
    ...(selectedTagIds.length > 0 ? { tagIds: selectedTagIds } : {}),
    ...(kind ? { kind } : {}),
    sortBy,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    page,
    pageSize: CORK_BOARD_PAGINATION.defaultPageSize,
  }), [propertyId, kind, selectedTagIds, sortBy, debouncedSearch, page]);

  const shouldSkipQuery = !propertyId;

  return {
    selectedTagIds,
    sortBy,
    searchInput,
    debouncedSearch,
    page,
    hasActiveFilters,
    accumulationKey,
    queryParams,
    shouldSkipQuery,

    toggleTag,
    clearTags,
    setSortBy: handleSetSortBy,
    setSearch,
    clearSearch,
    resetFilters,
    loadNextPage,
    resetPage,
  } as const;
};
